import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import * as KeyCode from 'keycode-js';
import { Icon, LoadingSpinner } from 'Permafrost/index';
import { StyledButton, StyledMenu, StyledMenuButton } from './MenuButton.styles';
/**
 * Technically a Menu and Menu Button, this component offers a list
 * of actions for the user to choose from.
 *
 * This component should not be used for navigation.
 *
 * https://www.w3.org/TR/wai-aria-practices-1.1/#menubutton
 */
export function MenuButton(props) {
    const { className, disabled, id, open, options, processing, size = 'normal', title } = props;
    const [isOpen, setIsOpen] = useState(open || false);
    const buttonEl = useRef(null);
    const menuEl = useRef(null);
    useEffect(() => {
        let menuItems;
        const handleKeyPress = (ev) => {
            if (!menuItems)
                return null;
            const activeElement = document.activeElement;
            const firstItem = menuItems[0];
            const lastItem = menuItems[menuItems.length - 1];
            const nextSibling = activeElement.nextElementSibling;
            const previousSibling = activeElement.previousElementSibling;
            // up and down arrow keys will cycle through the different options
            if (ev.key === KeyCode.VALUE_DOWN) {
                if (activeElement !== lastItem) {
                    nextSibling.focus();
                }
                else {
                    firstItem.focus();
                }
            }
            else if (ev.key === KeyCode.VALUE_UP) {
                if (activeElement !== firstItem) {
                    previousSibling.focus();
                }
                else {
                    lastItem.focus();
                }
            }
            if (ev.key === KeyCode.VALUE_ESCAPE) {
                closeMenu();
            }
        };
        // once the menu opens, any click should close it
        const handleMenuOpenClick = () => {
            closeMenu();
        };
        if (isOpen) {
            // add click event listener after the current event loop completes to avoid closing the menu immediately
            setTimeout(() => {
                window.addEventListener('click', handleMenuOpenClick);
            }, 0);
            window.addEventListener('keydown', handleKeyPress);
            if (menuEl.current) {
                menuItems = menuEl.current.querySelectorAll('button');
                menuItems[0].focus();
            }
        }
        return () => {
            var _a;
            window.removeEventListener('keydown', handleKeyPress);
            window.removeEventListener('click', handleMenuOpenClick);
            (_a = buttonEl.current) === null || _a === void 0 ? void 0 : _a.focus();
        };
    }, [isOpen]);
    const openMenu = () => {
        setIsOpen(true);
    };
    const closeMenu = () => {
        setIsOpen(false);
    };
    const toggleMenu = () => {
        isOpen ? closeMenu() : openMenu();
    };
    const handleOptionClick = (option) => {
        props.handleSelection(option);
    };
    const processingText = typeof processing === 'string' ? processing : 'Processing';
    return (React.createElement(StyledMenuButton, { className: className, "data-cy": props['data-cy'], id: id },
        React.createElement(StyledButton, { ref: buttonEl, type: "button", "aria-haspopup": true, "aria-expanded": isOpen, className: classNames(processing, size), disabled: disabled, onClick: toggleMenu },
            processing && React.createElement(LoadingSpinner, null),
            React.createElement("span", { "data-cy": "menuTitleSpan", className: "menuButtonTitle", "aria-live": "polite" }, !!processing ? processingText : title),
            !processing && (React.createElement("span", { className: "caret" },
                React.createElement(Icon, { name: "fa-caret-down", size: ['1.2em'] })))),
        React.createElement(StyledMenu, { ref: menuEl, role: "menu", className: isOpen ? 'open' : '' }, options.map((option) => (React.createElement("button", { "data-cy": "menuItemBtn", key: option.value, type: "button", role: "menuitem", tabIndex: -1, onClick: () => handleOptionClick(option.value) }, option.text))))));
}
//# sourceMappingURL=MenuButton.js.map